import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { s3BucketsApi, S3BucketConfig, S3BucketEnvironment } from '../services/s3-buckets-api';
import { moodleApi } from '../services/moodle-api';

interface S3ExportDialogProps {
  open: boolean;
  onClose: () => void;
  sourceId: string;
  tenantId: string;
  courseId: string;
  activityName: string;
}

export const S3ExportDialog: React.FC<S3ExportDialogProps> = ({
  open,
  onClose,
  sourceId,
  tenantId,
  courseId,
  activityName,
}) => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [buckets, setBuckets] = useState<S3BucketConfig[]>([]);
  const [environments, setEnvironments] = useState<S3BucketEnvironment[]>([]);
  const [selectedBucketId, setSelectedBucketId] = useState<string>('');
  const [selectedEnvId, setSelectedEnvId] = useState<string>('');
  const [customActivityName, setCustomActivityName] = useState<string>(activityName);

  useEffect(() => {
    if (open) {
      loadS3Configurations();
      setCustomActivityName(activityName);
      setError(null);
      setSuccess(null);
    }
  }, [open, activityName]);

  const loadS3Configurations = async () => {
    setLoading(true);
    setError(null);
    try {
      const [bucketsData, environmentsData] = await Promise.all([
        s3BucketsApi.listBuckets(),
        s3BucketsApi.listEnvironments(),
      ]);
      setBuckets(bucketsData);
      setEnvironments(environmentsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load S3 configurations');
    } finally {
      setLoading(false);
    }
  };

  const filteredEnvironments = environments.filter(
    env => !selectedBucketId || env.bucketId === selectedBucketId
  );

  const handleExport = async () => {
    if (!selectedBucketId || !selectedEnvId) {
      setError('Please select both a bucket and an environment');
      return;
    }

    setExporting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await moodleApi.exportCMI5ActivityToS3(
        sourceId,
        tenantId,
        courseId,
        selectedBucketId,
        selectedEnvId,
        customActivityName
      );

      setSuccess(`Activity exported successfully to ${result.s3Key}`);

      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export activity to S3');
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    if (!exporting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export CMI5 Activity to S3</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Activity Name"
              value={customActivityName}
              onChange={(e) => setCustomActivityName(e.target.value)}
              helperText="Name to use in S3 metadata"
            />

            <FormControl fullWidth>
              <InputLabel>S3 Bucket</InputLabel>
              <Select
                value={selectedBucketId}
                onChange={(e) => {
                  setSelectedBucketId(e.target.value);
                  setSelectedEnvId(''); // Reset environment selection
                }}
                label="S3 Bucket"
              >
                {buckets.map((bucket) => (
                  <MenuItem key={bucket.id} value={bucket.id}>
                    {bucket.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedBucketId}>
              <InputLabel>Environment</InputLabel>
              <Select
                value={selectedEnvId}
                onChange={(e) => setSelectedEnvId(e.target.value)}
                label="Environment"
              >
                {filteredEnvironments.map((env) => (
                  <MenuItem key={env.id} value={env.id}>
                    {env.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                This will extract the CMI5 activity from the Moodle pod and upload it to the selected S3 bucket with metadata preserved.
              </Alert>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={!selectedBucketId || !selectedEnvId || exporting || !!success}
        >
          {exporting ? <CircularProgress size={20} /> : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
